import React, { useState, useEffect, useMemo } from "react"
import { createPortal } from "react-dom"
import axios from "axios"

const PAYMENT_STEPS = {
  SELECT_METHOD: "SELECT_METHOD",
  NAIRA_DETAILS: "NAIRA_DETAILS",
  CRYPTO_DETAILS: "CRYPTO_DETAILS",
  USD_DETAILS: "USD_DETAILS",
  CAD_DETAILS: "CAD_DETAILS",
  CAD_JUICYWAY_INTERAC: "CAD_JUICYWAY_INTERAC",
  NAIRA_JUICYWAY_TRANSFER: "NAIRA_JUICYWAY_TRANSFER",
  NAIRA_JUICYWAY_CARD: "NAIRA_JUICYWAY_CARD",
  PAYMENT_FORM: "PAYMENT_FORM",
  PAYMENT_SUCCESS: "PAYMENT_SUCCESS",
}

// Map payment step to currency for our confirmation form (CAD/Naira Juicyway don't use this form)
const STEP_TO_CURRENCY = {
  [PAYMENT_STEPS.NAIRA_DETAILS]: "naira",
  [PAYMENT_STEPS.USD_DETAILS]: "usd",
  [PAYMENT_STEPS.CRYPTO_DETAILS]: "crypto",
}

const PAYMENT_FORM_CLOSED_MESSAGE =
  "Payment is currently closed. If you have paid already, kindly send an email to bitcoin@kudy.io"

const paymentMethods = [
  {
    id: "crypto",
    title: "Crypto Payment",
    description: "Deposit USDT, or USDC.",
    step: PAYMENT_STEPS.CRYPTO_DETAILS,
    icon: "₿",
    accent: "#f7931a",
  },
  {
    id: "naira",
    title: "Naira Transfer (VFD)",
    description: "Deposit Nigerian Naira.",
    step: PAYMENT_STEPS.NAIRA_DETAILS,
    icon: "₦",
    accent: "#4ade80",
  },
  // {
  //   id: "naira-juicyway-transfer",
  //   title: "Naira Transfer (powered by Juicyway)",
  //   description: "Deposit Nigerian Naira via bank transfer.",
  //   step: PAYMENT_STEPS.NAIRA_JUICYWAY_TRANSFER,
  // },
  {
    id: "naira-juicyway-card",
    title: "Naira Card Payment",
    description: "Deposit Nigerian Naira via card payment.",
    step: PAYMENT_STEPS.NAIRA_JUICYWAY_CARD,
    disabled: true,
    icon: "💳",
    accent: "#7c6cff",
  },
  {
    id: "usd",
    title: "USD",
    description: "Deposit US Dollars.",
    step: PAYMENT_STEPS.USD_DETAILS,
    icon: "$",
    accent: "#34d399",
  },
  {
    id: "cad",
    title: "CAD (Interac)",
    description: "Deposit Canadian Dollars.",
    step: PAYMENT_STEPS.CAD_JUICYWAY_INTERAC,
    icon: "🍁",
    accent: "#f87171",
  },
]

const CopyableValue = ({ value }) => {
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error("Failed to copy:", err)
    }
  }

  return (
    <span className="detail-value">
      <span className="detail-value-text">{value}</span>
      <button
        type="button"
        onClick={handleCopy}
        className={`copy-button ${copied ? "copied" : ""}`}
        title="Copy to clipboard"
      >
        {copied ? "Copied!" : "Copy"}
      </button>
    </span>
  )
}

const PaymentWarning = ({ title, children }) => (
  <div className="warning-message payment-warning">
    <div className="payment-warning-header">
      <span className="warning-icon">{title || "Important"}</span>
    </div>
    <div className="payment-warning-body">{children}</div>
  </div>
)

const PaymentMethodSelector = ({ onSelect }) => (
  <div className="payment-content">
    <h2 className="payment-title">Select Payment Method</h2>
    <p className="payment-subtitle">
      Choose your preferred payment method to proceed with your Bitcoin
      purchase.
    </p>
    <div className="payment-methods">
      {paymentMethods.map((method) => (
        <button
          key={method.id}
          className={`payment-method-button ${
            method.disabled ? "is-disabled" : ""
          }`}
          onClick={() => !method.disabled && onSelect(method.step)}
          disabled={method.disabled}
          style={{ "--method-accent": method.accent }}
        >
          <div className="payment-method-info">
            <div className="payment-method-title">
              {method.title}
              {method.disabled && (
                <span className="payment-method-soon">Soon</span>
              )}
            </div>
            <div className="payment-method-description">
              {method.description}
            </div>
          </div>
        </button>
      ))}
    </div>
  </div>
)

const BackButton = ({ onClick }) => (
  <button
    type="button"
    className="payment-change-method"
    onClick={onClick}
  >
    <span className="payment-change-method-icon" aria-hidden="true">
      ←
    </span>
    <span className="payment-change-method-text">All payment methods</span>
  </button>
)

// Shown while the modal is still fetching the confirmation-form
// availability flag — keeps the user from seeing account details
// before we know whether payments are open.
const AvailabilityLoading = ({ title }) => (
  <div className="payment-content payment-form-content">
    <h2 className="payment-title">{title}</h2>
    <p style={{ textAlign: "center", marginTop: "20px" }}>Loading...</p>
  </div>
)

// Shown in place of account details / the confirmation form when
// payments are currently closed.
const PaymentLockedNotice = ({ title, message }) => (
  <div className="payment-content payment-form-content">
    <h2 className="payment-title">{title}</h2>
    <div
      className="warning-message"
      style={{ marginTop: "20px", textAlign: "center" }}
    >
      <p>{message || PAYMENT_FORM_CLOSED_MESSAGE}</p>
    </div>
  </div>
)

const ErrorModal = ({ onClose, onRetry }) => (
  <div className="modal-overlay" style={{ zIndex: 10001 }}>
    <div className="modal-content" style={{ maxWidth: "400px" }}>
      <button className="modal-close" onClick={onClose}>
        ×
      </button>
      <div className="payment-content">
        <h2 className="payment-title" style={{ color: "#ff4444" }}>
          Payment Error
        </h2>
        <p style={{ marginBottom: "20px", textAlign: "center" }}>
          Something went wrong, please try again or contact support.
        </p>
        <div style={{ display: "flex", gap: "10px", justifyContent: "center" }}>
          {onRetry && (
            <button className="payment-confirm-button" onClick={onRetry}>
              Try Again
            </button>
          )}
          <button className="back-button" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  </div>
)

const PAYMENT_PROCESSING_SUBTEXT = `Your payment instruction has been recorded.

All transfers are reconciled after the 4th of the cycle. Verified balances are reflected on the 7th of every month.

Thank you for your participation this cycle.`

const PaymentSuccess = ({
  onClose,
  isLoading,
  onPaymentSuccess,
  isFromConfirmationForm,
}) => {
  // Call onPaymentSuccess when loading completes and success is shown (Juicyway path)
  React.useEffect(() => {
    if (!isLoading && onPaymentSuccess) {
      // Small delay to ensure the success UI is rendered
      const timer = setTimeout(() => {
        onPaymentSuccess()
      }, 100)
      return () => clearTimeout(timer)
    }
  }, [isLoading, onPaymentSuccess])

  return (
    <div className="payment-content">
      <div style={{ textAlign: "center", padding: "20px 0" }}>
        {isLoading ? (
          <>
            <div
              style={{
                width: "64px",
                height: "64px",
                margin: "0 auto 20px",
                border: "4px solid #f3f3f3",
                borderTop: "4px solid #4caf50",
                borderRadius: "50%",
                animation: "spin 1s linear infinite",
              }}
            />
            <style>
              {`
                @keyframes spin {
                  0% { transform: rotate(0deg); }
                  100% { transform: rotate(360deg); }
                }
              `}
            </style>
            <h2 className="payment-title">Processing...</h2>
            <p style={{ marginTop: "10px", marginBottom: "30px" }}>
              Confirming your payment...
            </p>
          </>
        ) : isFromConfirmationForm ? (
          <>
            <h2 className="payment-title">Payment Processing</h2>
            <p
              style={{
                marginTop: "10px",
                marginBottom: "30px",
                lineHeight: 1.6,
                whiteSpace: "pre-line",
              }}
            >
              {PAYMENT_PROCESSING_SUBTEXT}
            </p>
            <button className="payment-confirm-button" onClick={onClose}>
              Close
            </button>
          </>
        ) : (
          <>
            <div className="success-ring" aria-hidden="true">
              <span className="success-ring-core" />
            </div>
            <h2 className="payment-title success-title">Payment Successful</h2>
            <p style={{ marginTop: "10px", marginBottom: "30px" }}>
              Your payment has been processed successfully.
            </p>
            <button className="payment-confirm-button" onClick={onClose}>
              Close
            </button>
          </>
        )}
      </div>
    </div>
  )
}

const AMOUNT_INPUT_REGEX = /^\d*\.?\d*$/
const AMOUNT_VALID_REGEX = /^\d+(\.\d{0,20})?$/

const isValidAmount = (value) => {
  const trimmed = (value || "").trim()
  if (!trimmed) return false
  if (!AMOUNT_VALID_REGEX.test(trimmed)) return false
  const num = Number(trimmed)
  return Number.isFinite(num) && num > 0
}

const PaymentForm = ({ paymentMethod, onClose, onSuccess }) => {
  const currency = STEP_TO_CURRENCY[paymentMethod]
  const baseUrl = process.env.REACT_APP_BASE_URL

  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)
  const [fieldErrors, setFieldErrors] = useState({})

  const defaultName = [
    localStorage.getItem("user_first_name") || "",
    localStorage.getItem("user_last_name") || "",
  ]
    .filter(Boolean)
    .join(" ")
  const defaultEmail = localStorage.getItem("user_email") || ""

  const [name, setName] = useState(defaultName)
  const [email, setEmail] = useState(defaultEmail)
  // Track fetch state so Submit can wait for the identity to arrive.
  // The Name / Email inputs are intentionally NEVER surfaced to the
  // user — if the fetch fails we retry rather than unhiding, because
  // a race between profile load and form submission was showing users
  // "Name/Email is required" on a form that has no visible field.
  const [profileLoading, setProfileLoading] = useState(
    !defaultName || !defaultEmail,
  )

  // Single-flight, retrying profile fetcher. Resolves with the merged
  // name and email (or empty strings if we truly can't reach the API).
  // Persists to localStorage so a subsequent dashboard visit is warm.
  const fetchProfile = React.useCallback(async () => {
    const token = localStorage.getItem("auth_key")
    if (!baseUrl || !token) return { name: "", email: "" }
    const MAX_ATTEMPTS = 4
    let lastErr
    for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
      try {
        const { data } = await axios.get(`${baseUrl}/profile`, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 8000,
        })
        const first = data?.name?.toString().split(" ")[0]?.trim() || ""
        const last =
          data?.last_name?.toString().trim() ||
          data?.name?.toString().split(" ").slice(1).join(" ").trim() ||
          ""
        const fetchedEmail = data?.email?.toString().trim() || ""
        if (first) localStorage.setItem("user_first_name", first)
        if (last) localStorage.setItem("user_last_name", last)
        if (fetchedEmail) localStorage.setItem("user_email", fetchedEmail)
        const merged = [first, last].filter(Boolean).join(" ")
        return { name: merged, email: fetchedEmail }
      } catch (err) {
        lastErr = err
        // Exponential-ish backoff: 300ms, 800ms, 2000ms between retries.
        const delay = [300, 800, 2000][attempt] ?? 2000
        await new Promise((r) => setTimeout(r, delay))
      }
    }
    console.error("PaymentModal profile fetch failed after retries:", lastErr)
    return { name: "", email: "" }
  }, [baseUrl])

  // Initial prefill after mount: if we don't already have both fields
  // in state (localStorage was empty for at least one), kick the fetch.
  useEffect(() => {
    if (!profileLoading) return
    let cancelled = false
    ;(async () => {
      const p = await fetchProfile()
      if (cancelled) return
      if (p.name) setName(p.name)
      if (p.email) setEmail(p.email)
      setProfileLoading(false)
    })()
    return () => { cancelled = true }
  }, [profileLoading, fetchProfile])
  const [amount, setAmount] = useState("")
  const [transactionHash, setTransactionHash] = useState("")
  const [network, setNetwork] = useState("")
  const [bank, setBank] = useState("")

  const validateForm = () => {
    const errs = {}
    // Name and Email are prefilled from the profile API — the inputs are
    // hidden, so they should never appear in the visible errors list.
    // handleSubmit runs its own refetch + hard error before it gets here.
    if (!amount.trim()) errs.amount = "Amount is required"
    else if (!isValidAmount(amount))
      errs.amount =
        "Amount must be a decimal or float (up to 20 decimal places)"
    if (currency === "crypto") {
      if (!transactionHash.trim())
        errs.transactionHash = "Transaction hash is required"
      if (!network.trim()) errs.network = "Network is required"
    } else {
      if (!bank.trim()) errs.bank = "Bank is required"
    }
    setFieldErrors(errs)
    return Object.keys(errs).length === 0
  }

  const handleAmountChange = (e) => {
    const v = e.target.value
    if (v === "" || AMOUNT_INPUT_REGEX.test(v)) setAmount(v)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError(null)
    setFieldErrors({})

    // Belt-and-braces: if the initial fetch lost the race with the
    // user's click, refetch once more here BEFORE validating.  The Name
    // / Email inputs are hidden, so we can't ask the user to type them
    // in — we have to get them from the profile API.
    let effectiveName = name
    let effectiveEmail = email
    if (!effectiveName.trim() || !effectiveEmail.trim()) {
      setSubmitting(true)
      try {
        const p = await fetchProfile()
        if (p.name) {
          effectiveName = p.name
          setName(p.name)
        }
        if (p.email) {
          effectiveEmail = p.email
          setEmail(p.email)
        }
      } finally {
        setSubmitting(false)
      }
      if (!effectiveName.trim() || !effectiveEmail.trim()) {
        setError(
          "We couldn't load your account details. Please refresh the page and try again.",
        )
        return
      }
    }

    if (!validateForm()) return
    const token = localStorage.getItem("auth_key")
    if (!token) {
      setError("Please log in again.")
      return
    }
    const payload = {
      name: effectiveName.trim(),
      email: effectiveEmail.trim(),
      currency,
      amount: amount.trim(),
    }
    if (currency === "crypto") {
      payload.transactionHash = transactionHash.trim()
      payload.network = network.trim()
    } else {
      payload.bank = bank.trim()
    }
    setSubmitting(true)
    try {
      const res = await axios.post(
        `${baseUrl}/payments/confirmation`,
        payload,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      )
      if (res.data && res.data.success && onSuccess) {
        onSuccess()
      } else {
        onSuccess()
      }
    } catch (err) {
      const msg =
        err.response?.data?.error || err.message || "Submission failed."
      setError(msg)
    } finally {
      setSubmitting(false)
    }
  }

  // Name / Email are hidden fields — Submit is gated only on the
  // visible inputs plus the initial profile load. handleSubmit will
  // refetch the profile on the fly if either is still missing.
  const allFieldsFilled =
    !profileLoading &&
    isValidAmount(amount) &&
    (currency === "crypto"
      ? transactionHash.trim() && network.trim()
      : bank.trim())

  const getSubmitDisabledReasons = () => {
    const reasons = []
    if (profileLoading) reasons.push("Loading your account details…")
    // Skip Name / Email — they're hidden fields that the fetch handles;
    // handleSubmit refetches them if missing at click time.
    if (!amount.trim()) reasons.push("Amount is required")
    else if (!isValidAmount(amount))
      reasons.push(
        "Amount must be a decimal or float (up to 20 decimal places)",
      )
    if (currency === "crypto") {
      if (!transactionHash.trim()) reasons.push("Transaction hash is required")
      if (!network.trim()) reasons.push("Network is required")
    } else {
      if (!bank.trim()) reasons.push("Bank is required")
    }
    return reasons
  }

  // Availability gating (loading + closed states) is handled at the
  // PaymentModal level so the locked notice shows before account
  // details, not after the user has already seen them.

  return (
    <div className="payment-content payment-form-content">
      <h2 className="payment-title">Payment Confirmation</h2>
      {/* <p className="payment-subtitle">
        Enter amount as decimal or float (up to 20 decimal places), e.g. 10.5,
        15.12345678901234567890
      </p> */}
      <form
        onSubmit={handleSubmit}
        className="form-container"
        style={{ maxWidth: "100%" }}
      >
        <div className="payment-details">
          {/* <div
            className="detail-row"
            style={{ flexDirection: "column", alignItems: "stretch" }}
          >
            <label className="detail-label">Name *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your name"
              required
              className="amount-input"
              style={{ width: "100%", marginTop: "4px" }}
            />
            {fieldErrors.name && (
              <span
                style={{ color: "#ff4444", fontSize: "12px", marginTop: "2px" }}
              >
                {fieldErrors.name}
              </span>
            )}
          </div>
          <div
            className="detail-row"
            style={{ flexDirection: "column", alignItems: "stretch" }}
          >
            <label className="detail-label">Email *</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Your email"
              required
              className="amount-input"
              style={{ width: "100%", marginTop: "4px" }}
            />
            {fieldErrors.email && (
              <span
                style={{ color: "#ff4444", fontSize: "12px", marginTop: "2px" }}
              >
                {fieldErrors.email}
              </span>
            )}
          </div> */}
          <div
            className="detail-row"
            style={{ flexDirection: "column", alignItems: "stretch" }}
          >
            <label className="detail-label">Amount *</label>
            <input
              type="text"
              inputMode="decimal"
              value={amount}
              onChange={handleAmountChange}
              placeholder="Amount"
              required
              className="amount-input"
              style={{ width: "100%", marginTop: "4px" }}
            />
            {fieldErrors.amount && (
              <span
                style={{ color: "#ff4444", fontSize: "12px", marginTop: "2px" }}
              >
                {fieldErrors.amount}
              </span>
            )}
          </div>
          {currency === "crypto" && (
            <>
              <div
                className="detail-row"
                style={{ flexDirection: "column", alignItems: "stretch" }}
              >
                <label className="detail-label">Transaction Hash *</label>
                <input
                  type="text"
                  value={transactionHash}
                  onChange={(e) => setTransactionHash(e.target.value)}
                  placeholder="Transaction Hash"
                  required
                  className="amount-input"
                  style={{ width: "100%", marginTop: "4px" }}
                />
                {fieldErrors.transactionHash && (
                  <span
                    style={{
                      color: "#ff4444",
                      fontSize: "12px",
                      marginTop: "2px",
                    }}
                  >
                    {fieldErrors.transactionHash}
                  </span>
                )}
              </div>
              <div
                className="detail-row"
                style={{ flexDirection: "column", alignItems: "stretch" }}
              >
                <label className="detail-label">
                  Network Used (e.g. ETH, BSC, TRX) *
                </label>
                <input
                  type="text"
                  value={network}
                  onChange={(e) => setNetwork(e.target.value)}
                  placeholder="e.g. ETH, BSC, TRX"
                  required
                  className="amount-input"
                  style={{ width: "100%", marginTop: "4px" }}
                />
                {fieldErrors.network && (
                  <span
                    style={{
                      color: "#ff4444",
                      fontSize: "12px",
                      marginTop: "2px",
                    }}
                  >
                    {fieldErrors.network}
                  </span>
                )}
              </div>
            </>
          )}
          {(currency === "usd" || currency === "naira") && (
            <div
              className="detail-row"
              style={{ flexDirection: "column", alignItems: "stretch" }}
            >
              <label className="detail-label">Bank *</label>
              <input
                type="text"
                value={bank}
                onChange={(e) => setBank(e.target.value)}
                placeholder="Your answer"
                required
                className="amount-input"
                style={{ width: "100%", marginTop: "4px" }}
              />
              {fieldErrors.bank && (
                <span
                  style={{
                    color: "#ff4444",
                    fontSize: "12px",
                    marginTop: "2px",
                  }}
                >
                  {fieldErrors.bank}
                </span>
              )}
            </div>
          )}
        </div>
        {error && (
          <p
            style={{ color: "#ff4444", marginTop: "10px", textAlign: "center" }}
          >
            {error}
          </p>
        )}
        {!allFieldsFilled && (
          <div
            style={{
              marginTop: "16px",
              padding: "12px",
              background: "#fff3cd",
              borderRadius: "6px",
              fontSize: "13px",
              color: "#856404",
            }}
          >
            <strong>Please fill in the following fields:</strong>
            <ul style={{ margin: "8px 0 0 0", paddingLeft: "20px" }}>
              {getSubmitDisabledReasons().map((reason, i) => (
                <li key={i}>{reason}</li>
              ))}
            </ul>
          </div>
        )}
        <div className="payment-actions">
          <button
            type="submit"
            className="payment-confirm-button"
            disabled={submitting || !allFieldsFilled}
          >
            {submitting ? "Submitting…" : "Submit"}
          </button>
        </div>
      </form>
    </div>
  )
}

const NairaDetails = ({ onPaymentConfirm }) => (
  <div className="payment-content">
    <h2 className="payment-title">Naira Transfer (VFD) Payment Details</h2>
    <div className="payment-details">
      <div className="detail-row">
        <span className="detail-label">Account Name</span>
        <CopyableValue value="Emperia Fronteira limited" />
      </div>
      <div className="detail-row">
        <span className="detail-label">Bank Name</span>
        <CopyableValue value="VFD" />
      </div>
      <div className="detail-row">
        <span className="detail-label">Account Number</span>
        <CopyableValue value="1001382345" />
      </div>
    </div>
    <PaymentWarning title="Urgent — read before paying">
      <p>
        Avoid cryptocurrency-related references in bank payments. Violations
        may result in removal from the program and funds returned at market
        price.
      </p>
    </PaymentWarning>
    <div className="payment-actions">
      <button
        type="button"
        className="payment-confirm-button"
        onClick={onPaymentConfirm}
      >
        I've Made Payment
      </button>
    </div>
  </div>
)

const CryptoDetails = ({ onPaymentConfirm }) => (
  <div className="payment-content">
    <h2 className="payment-title">Crypto Payment Details</h2>
    <div className="payment-details">
      <div className="detail-row">
        <span className="detail-label">
          EVM (ETH, BSC, Optimism, Arbitrum, Avax, etc)
        </span>
        <CopyableValue value="0x55471C37702581224f397327F41198623ccb1375" />
      </div>
      <div className="detail-row">
        <span className="detail-label">USDT TRX (TRC20)</span>
        <CopyableValue value="TEMnZYiu42eCdGwuYiuZn9d24qm8ykZCWm" />
      </div>
      <div className="detail-row">
        <span className="detail-label">USDC Solana</span>
        <CopyableValue value="4zee71WP42kpZFiUk47P6wcAKZLRwMXXaT1XbhEv96GQ" />
      </div>
    </div>
    <PaymentWarning title="Urgent — read before sending">
      <p>
        Do not send USDC to our TRX address — those funds are permanently lost.
      </p>
      <p>Other incorrect tokens will be refunded to the sender.</p>
      <ul>
        <li>Solana address: USDC only</li>
        <li>EVM address (ETH, BNB, etc.): USDT and USDC</li>
      </ul>
    </PaymentWarning>
    <div className="payment-actions">
      <button
        type="button"
        className="payment-confirm-button"
        onClick={onPaymentConfirm}
      >
        I've Made Payment
      </button>
    </div>
  </div>
)

const USDDetails = ({ onPaymentConfirm }) => (
  <div className="payment-content">
    <h2 className="payment-title">USD Payment Details</h2>
    <div className="payment-details">
      <div className="detail-row">
        <span className="detail-label">Account Name</span>
        <CopyableValue value="Emperia Fronteira Limited" />
      </div>
      <div className="detail-row">
        <span className="detail-label">Account Number</span>
        <CopyableValue value="215189863195" />
      </div>
      <div className="detail-row">
        <span className="detail-label">Bank Name</span>
        <CopyableValue value="Lead Bank" />
      </div>
      <div className="detail-row">
        <span className="detail-label">Address</span>
        <CopyableValue value="1801 Main St, Kansas City, MO, 64108-1932" />
      </div>
      <div className="detail-row">
        <span className="detail-label">Account Type</span>
        <CopyableValue value="Savings" />
      </div>
      <div className="detail-row">
        <span className="detail-label">Routing number</span>
        <CopyableValue value="101019644" />
      </div>
    </div>
    <PaymentWarning title="Urgent — read before paying">
      <p>
        Avoid cryptocurrency-related references in bank payments. Violations
        may result in permanent removal from the program and funds returned at
        market price.
      </p>
    </PaymentWarning>
    <div className="payment-actions">
      <button
        type="button"
        className="payment-confirm-button"
        onClick={onPaymentConfirm}
      >
        I've Made Payment
      </button>
    </div>
  </div>
)

const CADDetails = () => {
  // This component is no longer used, but kept for backward compatibility
  // CAD now uses JuicywayPayment directly
  return null
}

const JuicywayPayment = ({
  paymentType,
  onSuccess,
  onError,
  onPaymentSuccess,
  amount: initialAmount,
}) => {
  const [amount, setAmount] = useState(initialAmount || "")
  const [isLoading, setIsLoading] = useState(false)
  const [widgetReady, setWidgetReady] = useState(false)
  const [showErrorModal, setShowErrorModal] = useState(false)

  const baseUrl = process.env.REACT_APP_BASE_URL
  const juicywayKey = process.env.REACT_APP_JUICYWAY_PUBLIC_KEY
  const appName = process.env.REACT_APP_JUICYWAY_APP_NAME || "Project 31"
  const isLive = useMemo(
    () => process.env.REACT_APP_JUICYWAY_IS_LIVE !== "false",
    [],
  )

  // Determine currency and medium based on payment type
  const currency = paymentType === "interac" ? "CAD" : "NGN"
  const medium = paymentType === "interac" ? "cad" : "naira"

  // Check if Juicyway widget is loaded
  useEffect(() => {
    const checkWidget = () => {
      if (window?.Juicyway?.PayWithJuice) {
        setWidgetReady(true)
      } else {
        setTimeout(checkWidget, 100)
      }
    }
    checkWidget()
  }, [])

  const generateReference = () => {
    return `jw_${Date.now()}_${Math.random().toString(16).slice(2, 8)}`
  }

  const saveTransaction = async (fiatValue, medium = "naira") => {
    try {
      const token = localStorage.getItem("auth_key")
      if (!token) {
        console.error("No auth token found")
        return
      }

      const response = await axios.post(
        `${baseUrl}/transactions`,
        {
          fiat_value: fiatValue,
          medium: medium,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )

      console.log("Transaction saved successfully:", response.data)
      return response.data
    } catch (error) {
      console.error("Error saving transaction:", error)
      throw error
    }
  }

  // Silent auto-fill + submit of the payment confirmation form so that
  // Juicyway (Interac/CAD) flows produce the same record the other
  // payment methods produce via the visible PaymentForm.
  const submitPaymentConfirmation = async (fiatValue) => {
    try {
      const token = localStorage.getItem("auth_key")
      if (!token) {
        console.error("No auth token found for payment confirmation")
        return
      }

      const name = [
        localStorage.getItem("user_first_name") || "",
        localStorage.getItem("user_last_name") || "",
      ]
        .filter(Boolean)
        .join(" ")
      const email = localStorage.getItem("user_email") || ""

      const payload = {
        name,
        email,
        currency: "cad",
        amount: String(fiatValue),
        bank: "Interac",
      }

      const response = await axios.post(
        `${baseUrl}/payments/confirmation`,
        payload,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      )

      console.log("Payment confirmation submitted:", response.data)
      return response.data
    } catch (error) {
      console.error("Error submitting payment confirmation:", error)
      // Swallow — the Juicyway txn was already saved, we don't want to
      // surface this to the user since the UI path is silent.
    }
  }

  const handleMakePayment = (e) => {
    if (e) e.preventDefault()
    if (!baseUrl) {
      alert("Payment service is not configured. Please contact support.")
      return
    }

    const token = localStorage.getItem("auth_key")
    if (!token) {
      alert("Authentication required. Please log in again.")
      return
    }

    if (!widgetReady || !window?.Juicyway?.PayWithJuice) {
      alert("Payment widget is still loading. Please try again.")
      return
    }

    if (!juicywayKey) {
      alert("Juicyway API key is missing. Please contact support.")
      return
    }

    const numericAmount = Number(amount)
    if (!Number.isFinite(numericAmount) || numericAmount <= 0) {
      alert("Enter a valid amount greater than zero.")
      return
    }

    const amountInMinorUnits = Math.round(numericAmount * 100)
    const reference = generateReference()

    setIsLoading(true)

    // Build the payment method object based on payment type
    const paymentMethodType =
      paymentType === "card"
        ? "card"
        : paymentType === "interac"
          ? "interac"
          : "bank_account"

    // Build the payload object
    const paymentPayload = {
      onClose: () => {
        setIsLoading(false)
      },
      onSuccess: async (result) => {
        setIsLoading(false)
        // const paymentReference =
        //   result?.reference || result?.data?.reference || reference

        // Move to success step immediately (will show spinner)
        if (onSuccess) {
          onSuccess(true) // Pass true to indicate we're saving transaction
        }

        // Save transaction to database
        try {
          await saveTransaction(numericAmount, medium)
          console.log("Transaction saved successfully")

          // For the Interac/CAD path, auto-submit the payment
          // confirmation form silently so this flow matches the other
          // payment methods (which require the user to fill it manually).
          if (paymentType === "interac") {
            await submitPaymentConfirmation(numericAmount)
          }

          // Update success step to show success icon (pass false to indicate done)
          // onPaymentSuccess will be called by PaymentSuccess component when isLoading becomes false
          if (onSuccess) {
            onSuccess(false)
          }
        } catch (error) {
          console.error("Failed to save transaction:", error)
          // Still try to submit the confirmation for Interac, then show
          // success even if either record failed (payment itself succeeded).
          if (paymentType === "interac") {
            await submitPaymentConfirmation(numericAmount)
          }
          if (onSuccess) {
            onSuccess(false)
          }
        }
      },
      onError: (error) => {
        console.error("Juicyway payment error:", error)
        setIsLoading(false)
        setShowErrorModal(true)
        if (onError) {
          onError(error)
        }
      },
      reference,
      amount: amountInMinorUnits,
      currency: currency,
      description: "Community Contribution",
      isLive,
      key: juicywayKey,
      appName,
      customer: {
        email: localStorage.getItem("user_email") || undefined,
        first_name: "Customer" || localStorage.getItem("user_first_name") || "",
        last_name: "User" || localStorage.getItem("user_last_name") || "",
        phone_number: "+2348100000000",
      },
      paymentMethod: {
        type: paymentMethodType,
      },
      order: {
        identifier: `order-${reference}`,
        items: [
          {
            name: "Community Contribution",
            type: "digital",
            qty: 1,
            amount: amountInMinorUnits,
          },
        ],
      },
    }

    try {
      // Log the payment method configuration for debugging
      // console.log("Payment method type:", paymentMethodType)
      // console.log("Payment payload (excluding callbacks):", {
      //   reference,
      //   amount: amountInMinorUnits,
      //   currency: "NGN",
      //   paymentMethod: paymentPayload.paymentMethod,
      // })
      window.Juicyway.PayWithJuice(paymentPayload)
    } catch (error) {
      console.error("Error opening Juicyway widget:", error)
      setIsLoading(false)
      alert("Unable to start payment. Please try again.")
    }
  }

  return (
    <>
      <div className="payment-content">
        <h2 className="payment-title">
          {paymentType === "card"
            ? "Naira Card Payment"
            : paymentType === "interac"
              ? "CAD (Interac) Payment"
              : "Naira Transfer (powered by Juicyway)"}
        </h2>
        <p className="payment-subtitle">
          {paymentType === "bank_account" &&
            "After transfer, please wait for payment to be acknowledged before closing the widget."}{" "}
        </p>
        <form onSubmit={handleMakePayment}>
          <div className="payment-details">
            <div
              className="detail-row"
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "10px",
              }}
            >
              <span className="detail-label">Amount ({currency})</span>
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="Enter amount"
                className="amount-input"
                style={{
                  width: "100%",
                  textAlign: "center",
                  borderColor: Number(amount) <= 0 ? "#FF0000" : "#23272f",
                }}
                min={paymentType === "interac" ? "10" : "100"}
                step={paymentType === "interac" ? "10" : "100"}
                disabled={isLoading}
              />
            </div>
          </div>
          <div className="payment-actions">
            <button
              type="submit"
              className="payment-confirm-button"
              disabled={
                isLoading ||
                !amount ||
                !amount.trim() ||
                !Number.isFinite(Number(amount)) ||
                Number(amount) <= 0 ||
                !widgetReady
              }
            >
              {isLoading ? "Processing..." : "Make Payment"}
            </button>
          </div>
        </form>
      </div>
      {showErrorModal && (
        <ErrorModal
          onClose={() => setShowErrorModal(false)}
          onRetry={() => {
            setShowErrorModal(false)
            handleMakePayment()
          }}
        />
      )}
    </>
  )
}

// Steps that should be blocked when payments are closed. Covers
// account/wire details, the confirmation form, and the Juicyway
// widget entry points — when the cycle is closed, none of these
// should be reachable, even though Juicyway doesn't itself depend
// on the confirmation-form availability flag.
const AVAILABILITY_GATED_STEPS = {
  [PAYMENT_STEPS.NAIRA_DETAILS]: "Naira Transfer (VFD) Payment Details",
  [PAYMENT_STEPS.CRYPTO_DETAILS]: "Crypto Payment Details",
  [PAYMENT_STEPS.USD_DETAILS]: "USD Payment Details",
  [PAYMENT_STEPS.PAYMENT_FORM]: "Payment Confirmation",
  [PAYMENT_STEPS.CAD_JUICYWAY_INTERAC]: "CAD (Interac) Payment",
  [PAYMENT_STEPS.NAIRA_JUICYWAY_TRANSFER]: "Naira Transfer (powered by Juicyway)",
  [PAYMENT_STEPS.NAIRA_JUICYWAY_CARD]: "Naira Card Payment",
}

const PROGRESS_STEPS = ["Method", "Details", "Done"]

// Maps the active payment step onto the 3-stage progress bar.
const progressIndexFor = (step) => {
  if (step === PAYMENT_STEPS.SELECT_METHOD) return 0
  if (step === PAYMENT_STEPS.PAYMENT_SUCCESS) return 2
  return 1
}

const PaymentProgress = ({ step }) => (
  <div className="payment-progress">
    {PROGRESS_STEPS.map((label, i) => (
      <React.Fragment key={label}>
        <div
          className={`pp-step ${i <= step ? "active" : ""} ${
            i < step ? "done" : ""
          }`}
        >
          <span className="pp-dot">{i < step ? "✓" : i + 1}</span>
          <span className="pp-label">{label}</span>
        </div>
        {i < PROGRESS_STEPS.length - 1 && (
          <span className={`pp-bar ${i < step ? "filled" : ""}`} />
        )}
      </React.Fragment>
    ))}
  </div>
)

const PaymentModal = ({ isOpen, onClose, onPaymentSuccess }) => {
  const [currentStep, setCurrentStep] = useState(PAYMENT_STEPS.SELECT_METHOD)
  const [previousStep, setPreviousStep] = useState(null)
  const [isSavingTransaction, setIsSavingTransaction] = useState(false)
  const [isFromConfirmationForm, setIsFromConfirmationForm] = useState(false)
  const [availability, setAvailability] = useState({
    open: null,
    message: null,
    loading: true,
  })

  // Handle body scroll lock
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden"
    } else {
      document.body.style.overflow = "unset"
    }

    return () => {
      document.body.style.overflow = "unset"
    }
  }, [isOpen])

  // Reset state when modal is closed
  useEffect(() => {
    if (!isOpen) {
      setCurrentStep(PAYMENT_STEPS.SELECT_METHOD)
      setPreviousStep(null)
      setIsSavingTransaction(false)
      setIsFromConfirmationForm(false)
    }
  }, [isOpen])

  // Fetch availability as soon as the modal opens so the gated
  // steps (details + confirmation form) have an answer ready by
  // the time the user picks a payment method.
  useEffect(() => {
    if (!isOpen) return undefined

    const baseUrl = process.env.REACT_APP_BASE_URL
    if (!baseUrl) {
      setAvailability({
        open: false,
        message: PAYMENT_FORM_CLOSED_MESSAGE,
        loading: false,
      })
      return undefined
    }

    let cancelled = false
    setAvailability({ open: null, message: null, loading: true })

    axios
      .get(`${baseUrl}/payments/confirmation/availability`)
      .then((res) => {
        if (cancelled) return
        setAvailability({
          open: Boolean(res.data?.open),
          message: res.data?.message || null,
          loading: false,
        })
      })
      .catch(() => {
        if (cancelled) return
        setAvailability({
          open: false,
          message: PAYMENT_FORM_CLOSED_MESSAGE,
          loading: false,
        })
      })

    return () => {
      cancelled = true
    }
  }, [isOpen])

  const handlePaymentConfirm = () => {
    setPreviousStep(currentStep)
    setCurrentStep(PAYMENT_STEPS.PAYMENT_FORM)
  }

  const handleBack = () => {
    if (currentStep === PAYMENT_STEPS.PAYMENT_FORM && previousStep) {
      setCurrentStep(previousStep)
      setPreviousStep(null)
    } else {
      setCurrentStep(PAYMENT_STEPS.SELECT_METHOD)
    }
  }

  if (!isOpen) return null

  const showChangeMethod =
    currentStep !== PAYMENT_STEPS.SELECT_METHOD &&
    currentStep !== PAYMENT_STEPS.PAYMENT_SUCCESS

  const renderStep = () => {
    // Gate account-details and confirmation-form steps on the
    // availability check before falling through to the real screen,
    // so the locked notice is shown *before* any account info.
    const gatedTitle = AVAILABILITY_GATED_STEPS[currentStep]
    if (gatedTitle) {
      if (availability.loading) {
        return <AvailabilityLoading title={gatedTitle} />
      }
      if (!availability.open) {
        return (
          <PaymentLockedNotice
            title={gatedTitle}
            message={availability.message}
          />
        )
      }
    }

    switch (currentStep) {
      case PAYMENT_STEPS.SELECT_METHOD:
        return <PaymentMethodSelector onSelect={setCurrentStep} />
      case PAYMENT_STEPS.NAIRA_DETAILS:
        return (
          <NairaDetails onPaymentConfirm={handlePaymentConfirm} />
        )
      case PAYMENT_STEPS.CRYPTO_DETAILS:
        return (
          <CryptoDetails onPaymentConfirm={handlePaymentConfirm} />
        )
      case PAYMENT_STEPS.USD_DETAILS:
        return (
          <USDDetails onPaymentConfirm={handlePaymentConfirm} />
        )
      case PAYMENT_STEPS.CAD_DETAILS:
        return (
          <CADDetails onPaymentConfirm={handlePaymentConfirm} />
        )
      case PAYMENT_STEPS.CAD_JUICYWAY_INTERAC:
        return (
          <JuicywayPayment
            paymentType="interac"
            onSuccess={(isLoading) => {
              setIsFromConfirmationForm(false)
              setIsSavingTransaction(isLoading)
              setCurrentStep(PAYMENT_STEPS.PAYMENT_SUCCESS)
            }}
            onError={() => {
              // Error is handled by the error modal in JuicywayPayment
            }}
            onPaymentSuccess={onPaymentSuccess}
          />
        )
      case PAYMENT_STEPS.NAIRA_JUICYWAY_TRANSFER:
        return (
          <JuicywayPayment
            paymentType="bank_account"
            onSuccess={(isLoading) => {
              setIsFromConfirmationForm(false)
              setIsSavingTransaction(isLoading)
              setCurrentStep(PAYMENT_STEPS.PAYMENT_SUCCESS)
            }}
            onError={() => {
              // Error is handled by the error modal in JuicywayPayment
            }}
            onPaymentSuccess={onPaymentSuccess}
          />
        )
      case PAYMENT_STEPS.NAIRA_JUICYWAY_CARD:
        return (
          <JuicywayPayment
            paymentType="card"
            onSuccess={(isLoading) => {
              setIsFromConfirmationForm(false)
              setIsSavingTransaction(isLoading)
              setCurrentStep(PAYMENT_STEPS.PAYMENT_SUCCESS)
            }}
            onError={() => {
              // Error is handled by the error modal in JuicywayPayment
            }}
            onPaymentSuccess={onPaymentSuccess}
          />
        )
      case PAYMENT_STEPS.PAYMENT_FORM:
        return (
          <PaymentForm
            paymentMethod={previousStep}
            onClose={onClose}
            onSuccess={() => {
              setIsFromConfirmationForm(true)
              setCurrentStep(PAYMENT_STEPS.PAYMENT_SUCCESS)
              setPreviousStep(null)
              if (onPaymentSuccess) onPaymentSuccess()
            }}
          />
        )
      case PAYMENT_STEPS.PAYMENT_SUCCESS:
        return (
          <PaymentSuccess
            onClose={onClose}
            isLoading={isSavingTransaction}
            onPaymentSuccess={onPaymentSuccess}
            isFromConfirmationForm={isFromConfirmationForm}
          />
        )
      default:
        return null
    }
  }

  return createPortal(
    <div className="modal-overlay payment-modal-overlay">
      <div className="modal-content payment-modal">
        {currentStep === PAYMENT_STEPS.SELECT_METHOD && (
          <button
            type="button"
            className="modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        )}
        {showChangeMethod && (
          <div className="payment-modal-nav">
            <BackButton onClick={handleBack} />
          </div>
        )}
        <PaymentProgress step={progressIndexFor(currentStep)} />
        <div className="modal-scroll-content">{renderStep()}</div>
      </div>
    </div>,
    document.body,
  )
}

export default PaymentModal
